/**
 * FeatureIdeasSection (T35.7.1) — the Feedback-tab "Feature ideas" block: the
 * PUBLISHED-only idea cards a user upvotes, plus the propose-a-feature entry.
 * The real version of the prototype's `.prop` cards + `#propose-sheet`.
 *
 * Each card (the shared `FeatureIdeaCardView`) shows the idea title + description,
 * with a mutually-exclusive 👍 / 👎 vote (the upvote count rises by one only while
 * liked; re-tapping the same direction clears it). Casting a vote reveals the
 * "why" field — saved via the /why endpoint onto the vote row — and an upvote
 * reveals the backing offer (the T35.7.2 gated stub).
 *
 * "Propose a feature" opens the sheet (`ProposeFeatureSheet`): a live similar-
 * search whose matches render as the SAME full card, so the user upvotes / backs a
 * match instead of duplicating, or adds a new idea.
 *
 * Standalone feature (ADR-012): the whole consumer surface lives under
 * `features/feature-ideas/`; the Feedback tab mounts this section.
 *
 * Mode-independent (T39): Feedback does not consume App mode or location
 * context. This section renders by its own rules (loading / empty / cards) on
 * the Feedback tab, in any App mode. Token-only (CP5); mobile-first; ≥44px
 * targets.
 */

import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { getFeatureIdeas, type FeatureIdeaCard, type VoteDirection } from './api';
import { FeatureIdeaCardView } from './FeatureIdeaCardView';
import { ProposeFeatureSheet } from './ProposeFeatureSheet';

// FLAT section — header, sub, propose banner, and cards sit directly on the page
// surface (the proto drops the contained tinted ideas-panel wrapper). Token-only
// (CP5).
const sectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
  boxSizing: 'border-box',
};

// The section header is an eyebrow: an uppercase, letter-spaced label + the
// "Coming soon" pill on one row (the proto's `.fb-sectionhead`). The 💡 icon
// chip is dropped.
const headRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
};

const headTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-bold)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--color-text-primary)',
};

// The "Coming soon" pill (the proto's `.sh-tag`).
const headTagStyle: CSSProperties = {
  fontSize: 'var(--font-size-2xs)',
  fontWeight: 'var(--font-weight-bold)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--color-accent-primary)',
  backgroundColor: 'var(--color-accent-subtle)',
  borderWidth: 'var(--border-width-hairline)',
  borderStyle: 'solid',
  borderColor: 'var(--color-accent-secondary)',
  borderRadius: 'var(--radius-pill)',
  padding: '0.125rem var(--space-2)',
};

const headSubStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.45,
};

// The propose-a-feature entry — a banner with a filled button (the proto's
// `.propose-banner`, matching the dev-support-banner pattern). PINNED: it sticks
// to the top of the scrolling Feedback content as the idea cards scroll under it
// (owner directive — overrides the proto's in-flow placement). Left 💡, middle
// two lines, right a filled "Propose" button (accent fill + inverse text, AA in
// both themes). Hover + focus-visible for the button live in responsive.css
// (inline styles can't carry pseudo-classes), keyed off the data-testid.
//
// The window/body is the scroll container (AppShell `<main>` has no overflow), so
// plain `position: sticky` pins against the viewport. `top` offsets by the live
// header height (`--app-header-height`, published by AppShell from the sticky
// header's real height incl. the native status-bar inset) so the banner pins
// JUST BELOW the header rather than sliding under it; the `0px` fallback covers
// the brief first paint before the ResizeObserver writes the var. `--z-sticky`
// (100, same layer as the header / nav / FAB) lifts it above the idle idea cards
// (no z-index) so they scroll under it. The `--color-accent-subtle` fill is a
// solid hex in both themes (#eef0fe / #222746, no alpha), so cards stay hidden
// behind it rather than bleeding through.
const proposeBannerStyle: CSSProperties = {
  position: 'sticky',
  top: 'var(--app-header-height, 0px)',
  zIndex: 'var(--z-sticky)' as unknown as number,
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  margin: '0 0 var(--space-3)',
  padding: 'var(--space-3)',
  borderWidth: 'var(--border-width-hairline)',
  borderStyle: 'solid',
  borderColor: 'var(--color-accent-secondary)',
  borderRadius: 'var(--radius-lg)',
  backgroundColor: 'var(--color-accent-subtle)',
  boxSizing: 'border-box',
};

const proposeIconStyle: CSSProperties = {
  flex: '0 0 auto',
  fontSize: 'var(--font-size-lg)',
  lineHeight: 1,
};

const proposeTextStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.0625rem',
};

const proposeTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
  lineHeight: 1.3,
};

const proposeSubStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.35,
};

const proposeBtnStyle: CSSProperties = {
  flex: '0 0 auto',
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const emptyStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-muted)',
};

// The disclaimer below the cards (the proto's `.prop-foot`).
const propFootStyle: CSSProperties = {
  marginTop: 'var(--space-2)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  lineHeight: 1.5,
};

export function FeatureIdeasSection(): JSX.Element {
  const [ideas, setIdeas] = useState<FeatureIdeaCard[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    getFeatureIdeas()
      .then((rows) => !cancelled && setIdeas(rows))
      .catch(() => {
        if (!cancelled) {
          setIdeas([]);
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  function onVoted(id: string, result: { my_vote: VoteDirection | null; upvotes: number }): void {
    setIdeas((prev) =>
      prev
        ? prev.map((p) => (p.id === id ? { ...p, my_vote: result.my_vote, upvotes: result.upvotes } : p))
        : prev,
    );
  }

  // T39 — Feedback is independent of App mode + location context. The section
  // renders by its own rules (loading / empty / cards); it never inspects mode.

  return (
    <section style={sectionStyle} aria-label="Feature ideas" data-testid="feature-ideas-section">
      <div style={headRowStyle}>
        <h2 style={headTitleStyle}>Feature ideas</h2>
        <span style={headTagStyle}>Coming soon</span>
      </div>
      <p style={headSubStyle}>
        Built from feedback like yours. <b>Upvote</b> what you want — back what matters most.
      </p>

      <div style={proposeBannerStyle} data-testid="feature-idea-propose-banner">
        <span style={proposeIconStyle} aria-hidden="true">💡</span>
        <span style={proposeTextStyle}>
          <span style={proposeTitleStyle}>Have an idea for Shopista?</span>
          <span style={proposeSubStyle}>Tell us what we should build next.</span>
        </span>
        <button
          type="button"
          style={proposeBtnStyle}
          onClick={() => setProposeOpen(true)}
          data-testid="feature-idea-propose-open"
        >
          Propose
        </button>
      </div>

      {ideas === null ? (
        <p style={emptyStyle} data-testid="feature-ideas-loading">
          Loading…
        </p>
      ) : ideas.length === 0 ? (
        <p style={emptyStyle} data-testid="feature-ideas-empty">
          {failed
            ? "We couldn't load ideas right now."
            : 'No published ideas yet — propose one above.'}
        </p>
      ) : (
        ideas.map((idea) => <FeatureIdeaCardView key={idea.id} idea={idea} onVoted={onVoted} />)
      )}

      <p style={propFootStyle} data-testid="feature-ideas-foot">
        We read every one — it shapes what&apos;s next. Votes, words, and backing stay private to your
        household; backing is a signal, not a pre-order.
      </p>

      <ProposeFeatureSheet
        open={proposeOpen}
        onClose={() => setProposeOpen(false)}
        onProposed={load}
        onUpvoted={load}
      />
    </section>
  );
}
